import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
    PlusOutlined,
    DeleteOutlined,
    UndoOutlined,
    PlayCircleOutlined,
    LoadingOutlined,
    CheckCircleOutlined,
    WarningOutlined,
    InfoCircleOutlined,
    SettingOutlined,
    SearchOutlined,
    LockOutlined,
} from "@ant-design/icons";

import { App, Select } from "antd";
import { fieldConfig, defaultFormValues } from "./fields";
import { validateBessForm } from "./validation";
import {
    fetchContainers,
    fetchPCS,
    saveSimulation,
    runSimulation,
    fetchSimulations,
    deleteSimulation,
    freezeSimulation,
} from "../../services/bessApi";

import SizingChart from "../../components/SizingChart";

export default function BessSizingPage() {
    const { message, modal } = App.useApp();
    const navigate = useNavigate();
    const location = useLocation();

    // Form and simulation state
    const [formValues, setFormValues] = useState(defaultFormValues);
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});

    // Step state: 1: Core, 2: Settings
    const [currentStep, setCurrentStep] = useState(1);
    const [purchaseYearsInput, setPurchaseYearsInput] = useState("");
    const [activeTab, setActiveTab] = useState("wizard"); // "wizard" or "history"
    const [searchQuery, setSearchQuery] = useState("");

    // API dropdown options state
    const [containerOptions, setContainerOptions] = useState([]);
    const [pcsOptions, setPcsOptions] = useState([]);
    const [loadingOptions, setLoadingOptions] = useState(true);
    const [pastSimulations, setPastSimulations] = useState([]);

    // Execution state
    const [isSaving, setIsSaving] = useState(false);
    const [isRunning, setIsRunning] = useState(false);
    const [loadedFromHistory, setLoadedFromHistory] = useState(false);

    const loadData = async () => {
        try {
            setLoadingOptions(true);
            const [containers, pcsList, sims] = await Promise.all([
                fetchContainers(),
                fetchPCS(),
                fetchSimulations(),
            ]);

            setContainerOptions(containers);
            setPcsOptions(pcsList);
            setPastSimulations((sims || []).sort((a, b) => b.id - a.id));

            // Prepopulate first options if available
            setFormValues((prev) => {
                const next = { ...prev };
                if (containers.length > 0 && !next.container_batch) {
                    next.container_batch = containers[0].id;
                }
                if (pcsList.length > 0 && !next.pcs) {
                    next.pcs = pcsList[0].id;
                }
                return next;
            });
        } catch (err) {
            console.error(err);
            message.error("Failed to load equipment configurations from backend.");
        } finally {
            setLoadingOptions(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    // Form input change handlers
    const handleInputChange = (name, value) => {
        const newValues = { ...formValues, [name]: value };
        if (name === "simulation_type" && value === "open_loop") {
            newValues.start_year = new Date().getFullYear();
            newValues.simulation_tenure = 25;
        }
        setFormValues(newValues);

        const validation = validateBessForm(newValues);
        setErrors(validation.errors);
    };

    const handleBlur = (name) => {
        setTouched({ ...touched, [name]: true });
        const validation = validateBessForm(formValues);
        setErrors(validation.errors);
    };

    const handlePurchaseYearsInputChange = (e) => {
        const val = e.target.value;
        setPurchaseYearsInput(val);

        const parsed = val.split(",")
            .map(item => item.trim())
            .filter(item => item !== "")
            .map(item => parseInt(item, 10))
            .filter(item => !isNaN(item));

        const uniqueSorted = Array.from(new Set(parsed)).sort((a, b) => a - b);
        handleInputChange("purchase_years", uniqueSorted);
    };

    // Reset form to defaults
    const handleReset = () => {
        modal.confirm({
            title: "Reset Configuration?",
            content: "Are you sure you want to reset all inputs to default configurations?",
            okText: "Reset",
            okType: "danger",
            cancelText: "Cancel",
            onOk: () => {
                setFormValues({
                    ...defaultFormValues,
                    container_batch: containerOptions[0]?.id || "",
                    pcs: pcsOptions[0]?.id || "",
                });
                setPurchaseYearsInput("");
                setErrors({});
                setTouched({});
                setCurrentStep(1);
                setLoadedFromHistory(false);
                message.success("Configuration reset to defaults.");
            }
        });
    };

    const handleStartNewSimulation = () => {
        const modalInstance = modal.info({
            title: "Start New Simulation",
            icon: <InfoCircleOutlined />,
            footer: null,
            closable: true,
            content: (
                <div className="mt-4 flex flex-col gap-4">
                    <p className="text-sm text-slate-600">Would you like to copy the current configuration for the new simulation or start fresh?</p>
                    <div className="flex gap-3 justify-end mt-2">
                        <button 
                            className="px-4 py-2 border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-lg text-sm font-semibold transition-colors cursor-pointer"
                            onClick={() => {
                                setFormValues({
                                    ...defaultFormValues,
                                    container_batch: containerOptions[0]?.id || "",
                                    pcs: pcsOptions[0]?.id || "",
                                });
                                setPurchaseYearsInput("");
                                setErrors({});
                                setTouched({});
                                setCurrentStep(1);
                                setLoadedFromHistory(false);
                                message.success("Started a fresh simulation.");
                                modalInstance.destroy();
                            }}
                        >
                            Start Fresh
                        </button>
                        <button 
                            className="px-4 py-2 bg-[#0052cc] text-white hover:bg-[#0043a4] rounded-lg text-sm font-semibold transition-colors cursor-pointer"
                            onClick={() => {
                                setFormValues(prev => ({ ...prev, id: undefined, name: prev.name ? prev.name + " (Copy)" : "New Simulation" }));
                                setLoadedFromHistory(false);
                                message.success("Configuration copied. You can now save it as a new simulation.");
                                modalInstance.destroy();
                            }}
                        >
                            Copy Configuration
                        </button>
                    </div>
                </div>
            )
        });
    };

    const reloadPastSimulations = async () => {
        try {
            const sims = await fetchSimulations();
            setPastSimulations((sims || []).sort((a, b) => b.id - a.id));
        } catch (err) {
            console.error("Failed to reload past simulations:", err);
        }
    };

    const handleLoadSimulation = async (sim) => {
        try {
            const isUse220Line = sim.use_220kv_loss !== undefined && sim.use_220kv_loss !== null
                ? Boolean(sim.use_220kv_loss)
                : ((sim.transmission_line_220kv_loss ?? sim.transmission_line_loss) != null && (sim.transmission_line_220kv_loss ?? sim.transmission_line_loss) !== ""
                    ? Number(sim.transmission_line_220kv_loss ?? sim.transmission_line_loss) > 0
                    : false);

            const isUse400Line = sim.use_400kv_loss !== undefined && sim.use_400kv_loss !== null
                ? Boolean(sim.use_400kv_loss)
                : (sim.transmission_line_400kv_loss != null && sim.transmission_line_400kv_loss !== ""
                    ? Number(sim.transmission_line_400kv_loss) > 0
                    : false);

            const isUse220Trans = sim.use_transformer_220_loss !== undefined && sim.use_transformer_220_loss !== null
                ? Boolean(sim.use_transformer_220_loss)
                : ((sim.transformer_220kv_loss ?? sim.transformer_220_loss) != null && (sim.transformer_220kv_loss ?? sim.transformer_220_loss) !== ""
                    ? Number(sim.transformer_220kv_loss ?? sim.transformer_220_loss) > 0
                    : false);

            const isUse400Trans = sim.use_transformer_400_loss !== undefined && sim.use_transformer_400_loss !== null
                ? Boolean(sim.use_transformer_400_loss)
                : ((sim.transformer_400kv_loss ?? sim.transformer_400_220_loss ?? sim.transformer_400_loss) != null && (sim.transformer_400kv_loss ?? sim.transformer_400_220_loss ?? sim.transformer_400_loss) !== ""
                    ? Number(sim.transformer_400kv_loss ?? sim.transformer_400_220_loss ?? sim.transformer_400_loss) > 0
                    : false);

            const rawVal220Line = sim.transmission_line_220kv_loss ?? sim.transmission_line_loss;
            const rawVal400Line = sim.transmission_line_400kv_loss;
            const rawVal220Trans = sim.transformer_220kv_loss ?? sim.transformer_220_loss;
            const rawVal400Trans = sim.transformer_400kv_loss ?? sim.transformer_400_220_loss ?? sim.transformer_400_loss;

            const newValues = {
                ...defaultFormValues,
                id: sim.id,
                name: sim.name || "",
                container_batch: sim.container_batch,
                pcs: sim.pcs,
                simulation_type: sim.simulation_type,
                operation_hour: Number(sim.operation_hour),
                start_year: Number(sim.start_year),
                simulation_tenure: Number(sim.simulation_tenure),
                energy_poi_mwh: Number(sim.energy_poi_mwh),
                container_nos: sim.container_nos != null ? Number(sim.container_nos) : "",
                pcs_nos: sim.pcs_nos != null ? Number(sim.pcs_nos) : "",
                min_aug_period: sim.min_aug_period != null ? Number(sim.min_aug_period) : "",
                min_aug_container: sim.min_aug_container != null ? Number(sim.min_aug_container) : "",
                max_storage_capacity: sim.max_storage_capacity != null ? Number(sim.max_storage_capacity) : "",

                use_220kv_loss: isUse220Line,
                use_400kv_loss: isUse400Line,
                use_transformer_220_loss: isUse220Trans,
                use_transformer_400_loss: isUse400Trans,

                transmission_line_220kv_loss: isUse220Line && rawVal220Line != null && rawVal220Line !== "" && Number(rawVal220Line) > 0 ? Number(rawVal220Line) : "",
                transmission_line_400kv_loss: isUse400Line && rawVal400Line != null && rawVal400Line !== "" && Number(rawVal400Line) > 0 ? Number(rawVal400Line) : "",
                transformer_220kv_loss: isUse220Trans && rawVal220Trans != null && rawVal220Trans !== "" && Number(rawVal220Trans) > 0 ? Number(rawVal220Trans) : "",
                transformer_400kv_loss: isUse400Trans && rawVal400Trans != null && rawVal400Trans !== "" && Number(rawVal400Trans) > 0 ? Number(rawVal400Trans) : "",

                solar_aux_power: sim.solar_aux_power !== null && sim.solar_aux_power !== undefined && sim.solar_aux_power !== "" ? Number(sim.solar_aux_power) : "",
                ht_ac_cable_loss: sim.ht_ac_cable_loss !== null && sim.ht_ac_cable_loss !== undefined ? Number(sim.ht_ac_cable_loss) : "",
                idt_loss: Number(sim.idt_loss),
                lt_ac_cable_loss: Number(sim.lt_ac_cable_loss),
                pcs_loss: Number(sim.pcs_loss),
                lt_dc_cable_loss: Number(sim.lt_dc_cable_loss),
                bess_plant_unavailability: Number(sim.bess_plant_unavailability),
                purchase_years: sim.purchase_years || [],
                tl_volt_stage_1: sim.tl_volt_stage_1 != null ? Number(sim.tl_volt_stage_1) : 220,
                tl_volt_stage_2: sim.tl_volt_stage_2 != null ? Number(sim.tl_volt_stage_2) : 400,
            };
            setFormValues(newValues);
            if (sim.purchase_years && sim.purchase_years.length > 0) {
                const formatted = sim.purchase_years.map(item => {
                    if (typeof item === 'object' && item !== null) {
                        return `${item.year}`;
                    }
                    return `${item}`;
                }).join(", ");
                setPurchaseYearsInput(formatted);
            } else {
                setPurchaseYearsInput("");
            }
            setErrors({});
            setTouched({});
            setActiveTab("wizard");
            setCurrentStep(1); // Load directly to form step
            setLoadedFromHistory(true);

            const displayName = sim.name ? sim.name : `#${sim.id}`;
            message.info(`Loaded configuration for Simulation ${displayName}.`);
        } catch (err) {
            console.error(err);
            message.error("Failed to load details of the selected simulation configuration.");
        }
    };

    useEffect(() => {
        if (location.state?.loadSim) {
            handleLoadSimulation(location.state.loadSim);
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state]);

    const handleDeleteSimulation = (sim) => {
        const displayName = sim.name ? sim.name : `Simulation #${sim.id}`;
        modal.confirm({
            title: "Delete Simulation?",
            content: `Are you sure you want to delete ${displayName}? This action cannot be undone.`,
            okText: "Delete",
            okType: "danger",
            cancelText: "Cancel",
            onOk: async () => {
                try {
                    await deleteSimulation(sim.id);
                    message.success("Simulation deleted successfully.");
                    setPastSimulations((prev) => prev.filter((s) => s.id !== sim.id));
                } catch (err) {
                    message.error(err.message || "Failed to delete simulation.");
                }
            }
        });
    };

    const handleFreezeSimulation = (sim) => {
        const displayName = sim.name ? sim.name : `Simulation #${sim.id}`;
        modal.confirm({
            title: "Freeze Simulation?",
            content: `Are you sure you want to freeze ${displayName}? Once frozen, it can no longer be edited.`,
            okText: "Freeze",
            okType: "primary",
            cancelText: "Cancel",
            onOk: async () => {
                try {
                    await freezeSimulation(sim.id);
                    message.success("Simulation frozen successfully.");
                    setPastSimulations((prev) =>
                        prev.map((s) => (s.id === sim.id ? { ...s, is_frozen: true } : s))
                    );
                } catch (err) {
                    message.error(err.message || "Failed to freeze simulation.");
                }
            }
        });
    };

    // Save draft endpoint
    const handleSaveDraft = async () => {
        setIsSaving(true);
        try {
            const valLine220 = formValues.transmission_line_220kv_loss;
            const valLine400 = formValues.transmission_line_400kv_loss;
            const valTrans220 = formValues.transformer_220kv_loss;
            const valTrans400 = formValues.transformer_400kv_loss;

            const isUseLine220 = Boolean(formValues.use_220kv_loss);
            const isUseLine400 = Boolean(formValues.use_400kv_loss);
            const isUseTrans220 = Boolean(formValues.use_transformer_220_loss);
            const isUseTrans400 = Boolean(formValues.use_transformer_400_loss);

            const numLine220 = isUseLine220 ? (Number(valLine220) || 0) : 0;
            const numLine400 = isUseLine400 ? (Number(valLine400) || 0) : 0;

            const numTrans220 = isUseTrans220 ? (Number(valTrans220) || 0) : 0;
            const numTrans400 = isUseTrans400 ? (Number(valTrans400) || 0) : 0;

            const payload = {
                ...formValues,
                solar_aux_power: Number(formValues.solar_aux_power) || 0,

                use_220kv_loss: isUseLine220,
                use_400kv_loss: isUseLine400,
                transmission_line_220kv_loss: numLine220,
                transmission_line_400kv_loss: numLine400,

                use_transformer_220_loss: isUseTrans220,
                use_transformer_400_loss: isUseTrans400,
                transformer_220kv_loss: numTrans220,
                transformer_400kv_loss: numTrans400,

                ht_ac_cable_loss: Number(formValues.ht_ac_cable_loss) || 0,
            };
            delete payload.transmission_line_loss;
            delete payload.pss_transformer_loss;
            delete payload.transformer_220_loss;
            delete payload.transformer_400_loss;
            delete payload.transformer_400_220_loss;
            const savedSim = await saveSimulation(payload);
            setFormValues((prev) => ({ ...prev, id: savedSim.id }));
            message.success(`Draft saved successfully (Simulation Draft #${savedSim.id})`);
            reloadPastSimulations();
            return savedSim.id;
        } catch (err) {
            message.error(err.message || "Failed to save BESS simulation draft.");
            return null;
        } finally {
            setIsSaving(false);
        }
    };

    // Run Sizing Analysis endpoint
    const handleRunAnalysis = async () => {
        // Validate full form
        const validation = validateBessForm(formValues);
        if (!validation.isValid) {
            setErrors(validation.errors);
            message.error("Please correct the validation errors in the form before running simulation.");
            return;
        }

        setIsRunning(true);
        try {
            // Save first
            const simulationId = await handleSaveDraft();
            if (!simulationId) return;

            // Run
            await runSimulation(simulationId);
            message.success("Sizing simulation executed successfully!");
            reloadPastSimulations();
            navigate(`/bess-sizing/${simulationId}`);
        } catch (err) {
            message.error(err.message || "Sizing simulation execution failed.");
        } finally {
            setIsRunning(false);
        }
    };

    // Stepper navigation and validation
    const validateStep = (step) => {
        const tempErrors = validateBessForm(formValues).errors;
        const stepFields = {
            1: [
                "container_batch", "pcs", "simulation_type", "operation_hour",
                "start_year", "simulation_tenure", "energy_poi_mwh",
                ...(formValues.simulation_type === "closed_loop" ? ["purchase_years"] : []),
                "container_nos", "pcs_nos", "transmission_line_220kv_loss", "transformer_400kv_loss", "solar_aux_power", "transformer_220_loss", "transformer_400_loss", "ht_ac_cable_loss", "idt_loss", "lt_ac_cable_loss", "pcs_loss", "lt_dc_cable_loss", "bess_plant_unavailability"
            ]
        };

        const currentFields = stepFields[step] || [];
        const currentErrors = {};
        let isValid = true;
        currentFields.forEach((field) => {
            if (tempErrors[field]) {
                currentErrors[field] = tempErrors[field];
                isValid = false;
            }
        });

        setErrors((prev) => ({ ...prev, ...currentErrors }));

        const nextTouched = { ...touched };
        currentFields.forEach((field) => {
            nextTouched[field] = true;
        });
        setTouched(nextTouched);

        return isValid;
    };

    const handleContinue = () => {
        if (validateStep(currentStep)) {
            setCurrentStep((prev) => Math.min(prev + 1, 2));
        } else {
            message.error("Please fill all required inputs correctly before moving to the next step.");
        }
    };

    const handleBack = () => {
        setCurrentStep((prev) => Math.max(prev - 1, 1));
    };

    const steps = [
        { number: 1, title: "Project Details", desc: "Define the core components, timeframes, targets, and initial system losses." },
        { number: 2, title: "Review & Confirm", desc: "Verify your parameters and trigger the sizing simulation." }
    ];

    return (
        <div className="bess-container-redesign min-h-screen pt-2 px-6 pb-6 md:pt-4 md:px-8 md:pb-8 flex flex-col">
            {/* Scoped CSS block */}
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:ital,wght@0,100..900;1,100..900&display=swap');

                .bess-container-redesign {
                    background-color: #f7f9fb !important;
                    font-family: 'Inter', sans-serif !important;
                    color: #191c1e !important;
                }

                .bess-container-redesign button,
                .bess-container-redesign input,
                .bess-container-redesign select,
                .bess-container-redesign textarea {
                    font-family: 'Inter', sans-serif !important;
                }

                .bess-container-redesign h1,
                .bess-container-redesign h2,
                .bess-container-redesign h3,
                .bess-container-redesign h4 {
                    font-family: 'Inter', sans-serif !important;
                    font-weight: 600 !important;
                    border-left: none !important;
                    padding-left: 0 !important;
                    text-transform: none !important;
                    margin: 0 !important;
                    letter-spacing: -0.01em !important;
                }

                .bess-container-redesign .headline-lg {
                    font-size: 30px !important;
                    line-height: 38px !important;
                    letter-spacing: -0.02em !important;
                }

                .bess-layout-content .bess-container-redesign label {
                    color: #0c2340 !important;
                    font-family: 'Inter', sans-serif !important;
                    font-size: 11px !important;
                    font-weight: 700 !important;
                    text-transform: uppercase !important;
                    letter-spacing: 0.05em !important;
                    margin-bottom: 8px !important;
                    display: block !important;
                }

                .bess-layout-content .bess-container-redesign input:not([type="checkbox"]):not([type="radio"]) {
                    height: 44px !important;
                    padding: 0 14px !important;
                    font-size: 14px !important;
                    font-family: 'Inter', sans-serif !important;
                    font-weight: 400 !important;
                    color: #191c1e !important;
                    border: 1px solid #bfc7d2 !important;
                    border-radius: 8px !important;
                    background-color: #ffffff !important;
                    transition: all 0.2s ease-in-out !important;
                    box-shadow: none !important;
                    box-sizing: border-box !important;
                    width: 100% !important;
                }

                .bess-layout-content .bess-container-redesign input:not([type="checkbox"]):not([type="radio"]):hover {
                    border-color: #707881 !important;
                }

                .bess-layout-content .bess-container-redesign input:not([type="checkbox"]):not([type="radio"]):focus {
                    border-color: #0052CC !important;
                    box-shadow: 0 0 0 3px #dbeafe !important;
                    outline: none !important;
                }

                .bess-card {
                    background-color: #ffffff !important;
                    border-radius: 10px !important;
                    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.02) !important;
                    padding: 32px !important;
                    border: 1px solid #eceef0 !important;
                    box-sizing: border-box !important;
                }

                /* Ant Design Dropdown custom overrides */
                .bess-container-redesign .bess-antd-select.ant-select .ant-select-selector {
                    background-color: #ffffff !important;
                    border: 1px solid #bfc7d2 !important;
                    border-radius: 8px !important;
                    height: 44px !important;
                    display: flex !important;
                    align-items: center !important;
                    transition: all 0.2s ease-in-out !important;
                    box-shadow: none !important;
                }

                .bess-container-redesign .bess-antd-select.ant-select:hover .ant-select-selector {
                    border-color: #707881 !important;
                }

                .bess-container-redesign .bess-antd-select.ant-select-focused .ant-select-selector {
                    border-color: #0052CC !important;
                    box-shadow: 0 0 0 3px #dbeafe !important;
                }

                .ant-select-dropdown {
                    border-radius: 12px !important;
                    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.04) !important;
                    border: 1px solid #f1f5f9 !important;
                    padding: 6px !important;
                    z-index: 10000 !important;
                }

                .ant-select-dropdown .ant-select-item {
                    border-radius: 6px !important;
                    padding: 8px 12px !important;
                    font-size: 14px !important;
                    font-family: 'Inter', sans-serif !important;
                }

                .ant-select-dropdown .ant-select-item-option-selected {
                    background-color: #f0f9ff !important;
                    color: #0052CC !important;
                    font-weight: 600 !important;
                }

                /* Stepper design matching mockups */
                .bess-stepper {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    max-width: 960px;
                    margin: 0 auto 36px auto;
                    user-select: none;
                    padding: 0 8px;
                }

                .bess-step-item {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    flex-shrink: 0;
                }

                .bess-step-circle {
                    width: 32px;
                    height: 32px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 13px;
                    font-weight: 700;
                    transition: all 0.3s ease;
                }

                .bess-step-circle.active {
                    background-color: #0052CC !important;
                    color: #ffffff !important;
                }

                .bess-step-circle.completed {
                    background-color: #e0f2fe !important;
                    color: #0052CC !important;
                }

                .bess-step-circle.inactive {
                    background-color: #d8dadc !important;
                    color: #707881 !important;
                }

                .bess-step-label {
                    font-size: 13px;
                    font-weight: 600;
                    transition: all 0.3s ease;
                }

                .bess-step-label.active {
                    color: #0052CC !important;
                }

                .bess-step-label.inactive {
                    color: #707881 !important;
                }

                .bess-step-line {
                    flex: 1;
                    height: 2px;
                    background-color: #d8dadc;
                    margin: 0 16px;
                    min-width: 30px;
                    transition: all 0.3s ease;
                }

                .bess-step-line.active {
                    background-color: #0052CC;
                }

                .bess-btn-primary {
                    background-color: #0052CC !important;
                    color: #ffffff !important;
                    font-family: 'Inter', sans-serif !important;
                    font-weight: 600 !important;
                    font-size: 14px !important;
                    border: none !important;
                    border-radius: 8px !important;
                    height: 44px !important;
                    padding: 0 24px !important;
                    cursor: pointer !important;
                    transition: background-color 0.2s ease-in-out !important;
                    display: inline-flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                    gap: 8px !important;
                }

                .bess-btn-primary:hover {
                    background-color: #0073e6 !important;
                }

                .bess-btn-secondary {
                    background-color: #ffffff !important;
                    color: #707881 !important;
                    font-family: 'Inter', sans-serif !important;
                    font-weight: 500 !important;
                    font-size: 14px !important;
                    border: 1px solid #bfc7d2 !important;
                    border-radius: 8px !important;
                    height: 44px !important;
                    padding: 0 20px !important;
                    cursor: pointer !important;
                    transition: all 0.2s ease-in-out !important;
                    display: inline-flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                    gap: 8px !important;
                }

                .bess-btn-secondary:hover {
                    border-color: #707881 !important;
                    color: #191c1e !important;
                }

                .tech-badge {
                    font-family: 'Inter', sans-serif !important;
                    font-size: 10px !important;
                    font-weight: 600 !important;
                    background-color: #f2f4f6 !important;
                    color: #707881 !important;
                    padding: 2px 6px !important;
                    border-radius: 4px !important;
                    text-transform: uppercase !important;
                    display: inline-block;
                    margin-left: 6px;
                }

                .geist-val {
                    font-family: 'Inter', sans-serif !important;
                    font-weight: 500 !important;
                }

                .bess-table {
                    width: 100% !important;
                    border-collapse: collapse !important;
                }

                .bess-table th {
                    background-color: #f8fafc !important;
                    font-family: 'Inter', sans-serif !important;
                    font-size: 11px !important;
                    font-weight: 500 !important;
                    color: #707881 !important;
                    text-transform: uppercase !important;
                    letter-spacing: 0.05em !important;
                    padding: 10px 16px !important;
                    border-bottom: 1px solid #e6e8ea !important;
                    position: sticky !important;
                    top: 0 !important;
                    z-index: 10 !important;
                }

                .bess-table td {
                    padding: 12px 16px !important;
                    border-bottom: 1px solid #f2f4f6 !important;
                    font-size: 13.5px !important;
                    color: #191c1e !important;
                }

                input::placeholder {
                    color: #94a3b8 !important;
                    opacity: 0.55 !important;
                }
            `}</style>

            <div className="max-w-[1280px] mx-auto w-full flex-1">
                {/* Header Title Block with Inline Tabs */}
                <div className="flex flex-col md:flex-row md:items-center justify-between border-b border-[#bfc7d2] pb-4 gap-4 mb-6">
                    <h1 className="headline-lg font-bold text-[#0c2340]">
                        BESS Sizing Simulation
                    </h1>
                    <div className="flex items-center gap-3">
                        {loadedFromHistory && (
                            <button
                                type="button"
                                onClick={handleStartNewSimulation}
                                className="h-10 px-5 text-xs font-bold rounded-lg transition-all cursor-pointer bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100/50 shadow-sm flex items-center justify-center gap-1.5"
                                title="Start a new simulation from scratch or copy the current one"
                            >
                                Start New Simulation
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={() => setActiveTab("wizard")}
                            className={`h-10 px-5 text-xs font-bold rounded-lg transition-all cursor-pointer bg-white shadow-sm ${activeTab === "wizard"
                                ? "border border-blue-500 text-blue-600"
                                : "border border-slate-200 text-slate-600 hover:text-slate-800"
                                }`}
                        >
                            Simulation
                        </button>
                        <button
                            type="button"
                            onClick={() => setActiveTab("history")}
                            className={`h-10 px-5 text-xs font-bold rounded-lg transition-all cursor-pointer bg-white shadow-sm ${activeTab === "history"
                                ? "border border-blue-500 text-blue-600"
                                : "border border-slate-200 text-slate-600 hover:text-slate-800"
                                }`}
                        >
                            History ({pastSimulations.length})
                        </button>
                    </div>
                </div>

                {activeTab === "wizard" ? (
                    <div className="space-y-6">
                        {/* Stepper Header Progress Indicator */}
                        <div className="bess-stepper">
                            {steps.map((s, index) => (
                                <React.Fragment key={s.number}>
                                    <div className="bess-step-item">
                                        <div className={`bess-step-circle ${currentStep === s.number
                                            ? "active"
                                            : currentStep > s.number
                                                ? "completed"
                                                : "inactive"
                                            }`}>
                                            {currentStep > s.number ? "✓" : s.number}
                                        </div>
                                        <span className={`bess-step-label ${currentStep === s.number
                                            ? "active"
                                            : "inactive"
                                            }`}>
                                            {s.title}
                                        </span>
                                    </div>
                                    {index < steps.length - 1 && (
                                        <div className={`bess-step-line ${currentStep > s.number ? "active" : ""
                                            }`} />
                                    )}
                                </React.Fragment>
                            ))}
                        </div>

                        {/* Active Step Form Card */}
                        <div className="bess-card animate-fade-in">
                            <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4 mb-6">
                                <div className="flex-1">
                                    <span className="text-xs font-bold text-[#0052CC] tracking-wide block mb-1">
                                        Step {currentStep} of 2
                                    </span>
                                    <h2 className="text-2xl font-bold text-slate-800 mb-1">
                                        {steps[currentStep - 1].title}
                                    </h2>
                                    <p className="text-xs text-slate-400 font-medium">
                                        {steps[currentStep - 1].desc}
                                    </p>
                                </div>
                                <div className="flex items-center gap-2 pt-1">
                                    <Select
                                        placeholder="Select Past Simulation..."
                                        value={formValues.id || undefined}
                                        onChange={(simId) => {
                                            if (!simId) return;
                                            const matched = pastSimulations.find((s) => s.id === simId);
                                            if (matched) handleLoadSimulation(matched);
                                        }}
                                        allowClear
                                        onClear={() => window.location.reload()}
                                        style={{ minWidth: 260 }}
                                        className="bess-antd-select"
                                        options={pastSimulations.map((s) => ({
                                            value: s.id,
                                            label: `${s.name || `Simulation #${s.id}`} (${s.simulation_tenure || 25} Yrs)`
                                        }))}
                                    />
                                </div>
                            </div>

                            {/* Step Form Render Blocks */}
                            {currentStep === 1 && (
                                <div className="space-y-6 animate-fade-in">
                                    {/* 1. Core Parameters */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
                                        <div className="flex flex-col gap-1 md:col-span-2">
                                            <label>Simulation Name</label>
                                            <input
                                                type="text"
                                                value={formValues.name || ""}
                                                onChange={(e) => handleInputChange("name", e.target.value)}
                                                onBlur={() => handleBlur("name")}
                                                placeholder="Enter Project Name..."
                                            />
                                            {touched.name && errors.name && (
                                                <span className="text-[10px] text-red-500 font-semibold mt-1">
                                                    {errors.name}
                                                </span>
                                            )}
                                        </div>

                                        <div className="flex flex-col gap-1">
                                            <label>Container</label>
                                            <Select
                                                value={formValues.container_batch || undefined}
                                                disabled={loadingOptions}
                                                onChange={(val) => handleInputChange("container_batch", val)}
                                                placeholder="Select Container Batch..."
                                                className="bess-antd-select w-full"
                                            >
                                                {containerOptions.map((opt) => (
                                                    <Select.Option key={opt.id} value={opt.id}>
                                                        {opt.oem} - {parseFloat(opt.rated_capacity_mwh).toFixed(3)} MWh
                                                    </Select.Option>
                                                ))}
                                            </Select>
                                            {touched.container_batch && errors.container_batch && (
                                                <span className="text-[10px] text-red-500 font-semibold mt-1">
                                                    {errors.container_batch}
                                                </span>
                                            )}
                                        </div>

                                        <div className="flex flex-col gap-1">
                                            <label>PCS</label>
                                            <Select
                                                value={formValues.pcs || undefined}
                                                disabled={loadingOptions}
                                                onChange={(val) => handleInputChange("pcs", val)}
                                                placeholder="Select PCS System..."
                                                className="bess-antd-select w-full"
                                            >
                                                {pcsOptions.map((opt) => (
                                                    <Select.Option key={opt.id} value={opt.id}>
                                                        {opt.oem} - {opt.model}
                                                    </Select.Option>
                                                ))}
                                            </Select>
                                            {touched.pcs && errors.pcs && (
                                                <span className="text-[10px] text-red-500 font-semibold mt-1">
                                                    {errors.pcs}
                                                </span>
                                            )}
                                        </div>

                                        <div className="flex flex-col gap-1">
                                            <label>Simulation Type</label>
                                            <Select
                                                value={formValues.simulation_type}
                                                onChange={(val) => handleInputChange("simulation_type", val)}
                                                className="bess-antd-select w-full"
                                            >
                                                <Select.Option value="open_loop">Without Battery Augmentation</Select.Option>
                                                <Select.Option value="closed_loop">Manual Battery Augmentation</Select.Option>
                                                <Select.Option value="auto_closed_loop">Auto Battery Augmentation</Select.Option>
                                            </Select>
                                        </div>

                                        <div className="flex flex-col gap-1">
                                            <label>Storage Duration (Hours)</label>
                                            <input
                                                type="number"
                                                value={formValues.operation_hour}
                                                onChange={(e) => handleInputChange("operation_hour", e.target.value === "" ? "" : Number(e.target.value))}
                                                onBlur={() => handleBlur("operation_hour")}
                                                min="1"
                                                max="24"
                                                placeholder="4"
                                            />
                                            {touched.operation_hour && errors.operation_hour && (
                                                <span className="text-[10px] text-red-500 font-semibold mt-1">
                                                    {errors.operation_hour}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* 1b. Equipment Quantities */}
                                    {formValues.simulation_type !== "auto_closed_loop" && (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                                            <div className="flex flex-col gap-1">
                                                <label>Initial Container Quantity (Nos)</label>
                                                <input
                                                    type="number"
                                                    value={formValues.container_nos}
                                                    onChange={(e) => handleInputChange("container_nos", e.target.value === "" ? "" : Number(e.target.value))}
                                                    onBlur={() => handleBlur("container_nos")}
                                                />
                                                {touched.container_nos && errors.container_nos && (
                                                    <span className="text-[10px] text-red-500 font-semibold mt-1">
                                                        {errors.container_nos}
                                                    </span>
                                                )}
                                            </div>

                                            <div className="flex flex-col gap-1">
                                                <label>PCS Quantity (Nos)</label>
                                                <input
                                                    type="number"
                                                    value={formValues.pcs_nos}
                                                    onChange={(e) => handleInputChange("pcs_nos", e.target.value === "" ? "" : Number(e.target.value))}
                                                    onBlur={() => handleBlur("pcs_nos")}
                                                />
                                                {touched.pcs_nos && errors.pcs_nos && (
                                                    <span className="text-[10px] text-red-500 font-semibold mt-1">
                                                        {errors.pcs_nos}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* 2. Simulation Settings */}
                                    <div className="mt-12 space-y-5">
                                        <h4 className="form-section-title text-lg font-bold text-slate-800 uppercase tracking-wider pb-1">
                                            Simulation Settings
                                        </h4>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                            <div className="flex flex-col gap-1">
                                                <label>Start Year</label>
                                                <input
                                                    type="number"
                                                    value={formValues.start_year}
                                                    onChange={(e) => handleInputChange("start_year", e.target.value === "" ? "" : Number(e.target.value))}
                                                    onBlur={() => handleBlur("start_year")}
                                                />
                                                {touched.start_year && errors.start_year && (
                                                    <span className="text-[10px] text-red-500 font-semibold mt-1">
                                                        {errors.start_year}
                                                    </span>
                                                )}
                                            </div>

                                            <div className="flex flex-col gap-1">
                                                <label>Simulation Tenure (Years)</label>
                                                <input
                                                    type="number"
                                                    value={formValues.simulation_tenure}
                                                    onChange={(e) => handleInputChange("simulation_tenure", e.target.value === "" ? "" : Number(e.target.value))}
                                                    onBlur={() => handleBlur("simulation_tenure")}
                                                />
                                                {touched.simulation_tenure && errors.simulation_tenure && (
                                                    <span className="text-[10px] text-red-500 font-semibold mt-1">
                                                        {errors.simulation_tenure}
                                                    </span>
                                                )}
                                            </div>

                                            <div className="flex flex-col gap-1">
                                                <label>Contracted Energy @ POI (MWh)</label>
                                                <input
                                                    type="number"
                                                    value={formValues.energy_poi_mwh}
                                                    onChange={(e) => handleInputChange("energy_poi_mwh", e.target.value === "" ? "" : Number(e.target.value))}
                                                    onBlur={() => handleBlur("energy_poi_mwh")}
                                                />
                                                {touched.energy_poi_mwh && errors.energy_poi_mwh && (
                                                    <span className="text-[10px] text-red-500 font-semibold mt-1">
                                                        {errors.energy_poi_mwh}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {formValues.simulation_type === "auto_closed_loop" && (
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-2">
                                                <div className="flex flex-col gap-1">
                                                    <label>Min Augmentation Period (Years)</label>
                                                    <input
                                                        type="number"
                                                        value={formValues.min_aug_period}
                                                        onChange={(e) => handleInputChange("min_aug_period", e.target.value === "" ? "" : Number(e.target.value))}
                                                        onBlur={() => handleBlur("min_aug_period")}
                                                        placeholder="e.g. 2"
                                                    />
                                                    {touched.min_aug_period && errors.min_aug_period && (
                                                        <span className="text-[10px] text-red-500 font-semibold mt-1">
                                                            {errors.min_aug_period}
                                                        </span>
                                                    )}
                                                </div>

                                                <div className="flex flex-col gap-1">
                                                    <label>Min Augmentation Containers (Nos)</label>
                                                    <input
                                                        type="number"
                                                        value={formValues.min_aug_container}
                                                        onChange={(e) => handleInputChange("min_aug_container", e.target.value === "" ? "" : Number(e.target.value))}
                                                        onBlur={() => handleBlur("min_aug_container")}
                                                        placeholder="e.g. 1"
                                                    />
                                                    {touched.min_aug_container && errors.min_aug_container && (
                                                        <span className="text-[10px] text-red-500 font-semibold mt-1">
                                                            {errors.min_aug_container}
                                                        </span>
                                                    )}
                                                </div>

                                                <div className="flex flex-col gap-1">
                                                    <label>Max Storage Capacity (%)</label>
                                                    <input
                                                        type="number"
                                                        value={formValues.max_storage_capacity}
                                                        onChange={(e) => handleInputChange("max_storage_capacity", e.target.value === "" ? "" : Number(e.target.value))}
                                                        onBlur={() => handleBlur("max_storage_capacity")}
                                                        placeholder="e.g. 150"
                                                    />
                                                    {touched.max_storage_capacity && errors.max_storage_capacity && (
                                                        <span className="text-[10px] text-red-500 font-semibold mt-1">
                                                            {errors.max_storage_capacity}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Augmentation subforms */}
                                        {formValues.simulation_type === "closed_loop" && (
                                            <div className="border-t border-slate-100 pt-5 space-y-4">
                                                <h4 className="font-bold text-slate-800 text-[14px] flex items-center gap-1.5">
                                                    <SettingOutlined className="text-[#0052CC]" />
                                                    Manual Augmentation Schedule Editor
                                                </h4>
                                                <p className="text-xs text-slate-500 leading-relaxed">
                                                    Specify the project years and the number of containers to be purchased in those years.
                                                </p>

                                                {/* Rows container */}
                                                <div className="space-y-3">
                                                    {(formValues.purchase_years || []).map((item, idx) => {
                                                        const currentYear = typeof item === 'object' && item !== null ? item.year : item;
                                                        const currentContainers = typeof item === 'object' && item !== null ? item.containers : "";

                                                        return (
                                                            <div key={idx} className="flex items-end gap-4 py-2 bg-transparent">
                                                                <div className="flex-1 flex flex-col gap-1">
                                                                    <label className="text-[10px] font-bold text-slate-500 uppercase">Project Year</label>
                                                                    <input
                                                                        type="number"
                                                                        min="1"
                                                                        max={formValues.simulation_tenure || 50}
                                                                        value={currentYear ?? ""}
                                                                        onChange={(e) => {
                                                                            const val = e.target.value === "" ? "" : Number(e.target.value);
                                                                            const newPurchaseYears = [...(formValues.purchase_years || [])];
                                                                            if (typeof newPurchaseYears[idx] === 'object' && newPurchaseYears[idx] !== null) {
                                                                                newPurchaseYears[idx] = { ...newPurchaseYears[idx], year: val };
                                                                            } else {
                                                                                newPurchaseYears[idx] = { year: val, containers: 1 };
                                                                            }
                                                                            handleInputChange("purchase_years", newPurchaseYears);
                                                                        }}
                                                                        placeholder="Year e.g. 4"
                                                                        className="h-9 px-3 border border-slate-200 rounded-md text-xs focus:outline-none focus:border-blue-500 w-full bg-white"
                                                                    />
                                                                </div>
                                                                <div className="flex-1 flex flex-col gap-1">
                                                                    <label className="text-[10px] font-bold text-slate-500 uppercase">Container Quantity</label>
                                                                    <input
                                                                        type="number"
                                                                        min="1"
                                                                        value={currentContainers ?? ""}
                                                                        onChange={(e) => {
                                                                            const val = e.target.value === "" ? "" : Number(e.target.value);
                                                                            const newPurchaseYears = [...(formValues.purchase_years || [])];
                                                                            if (typeof newPurchaseYears[idx] === 'object' && newPurchaseYears[idx] !== null) {
                                                                                newPurchaseYears[idx] = { ...newPurchaseYears[idx], containers: val };
                                                                            } else {
                                                                                newPurchaseYears[idx] = { year: newPurchaseYears[idx], containers: val };
                                                                            }
                                                                            handleInputChange("purchase_years", newPurchaseYears);
                                                                        }}
                                                                        placeholder="Containers e.g. 5"
                                                                        className="h-9 px-3 border border-slate-200 rounded-md text-xs focus:outline-none focus:border-blue-500 w-full bg-white"
                                                                    />
                                                                </div>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        const newPurchaseYears = (formValues.purchase_years || []).filter((_, i) => i !== idx);
                                                                        handleInputChange("purchase_years", newPurchaseYears);
                                                                    }}
                                                                    className="h-9 w-9 mb-[5px] text-red-500 hover:text-red-700 bg-transparent border-none flex items-center justify-center cursor-pointer transition-colors shadow-none outline-none"
                                                                    title="Remove year"
                                                                >
                                                                    <DeleteOutlined style={{ fontSize: '16px' }} />
                                                                </button>
                                                            </div>
                                                        );
                                                    })}
                                                </div>

                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const current = formValues.purchase_years || [];
                                                        let nextYear = 1;
                                                        if (current.length > 0) {
                                                            const lastItem = current[current.length - 1];
                                                            const lastYear = typeof lastItem === 'object' && lastItem !== null ? lastItem.year : lastItem;
                                                            if (typeof lastYear === 'number' && !isNaN(lastYear)) {
                                                                nextYear = lastYear + 1;
                                                            }
                                                        }
                                                        handleInputChange("purchase_years", [...current, { year: nextYear, containers: 1 }]);
                                                    }}
                                                    className="h-9 px-4 border border-dashed border-[#0052cc] text-[#0052cc] hover:bg-blue-50/50 rounded-md text-xs font-semibold flex items-center gap-1.5 transition-all cursor-pointer bg-white"
                                                >
                                                    <PlusOutlined /> Add Augmentation
                                                </button>

                                                {touched.purchase_years && errors.purchase_years && (
                                                    <span className="text-[10px] text-red-500 font-semibold mt-1 block">
                                                        {errors.purchase_years}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    <div className="mt-12 space-y-5">
                                        <h4 className="form-section-title text-lg font-bold text-slate-800 uppercase tracking-wider pb-1">
                                            System Losses Settings
                                        </h4>
                                        <div className="flex flex-col md:flex-row gap-6 mb-4 p-4 border border-slate-200 rounded-lg bg-slate-50">
                                            <div className="flex flex-col gap-2">
                                                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Voltage Stage 1 (kV)</label>
                                                <div className="flex gap-4 items-center">
                                                    {[66, 110, 132, 220].map((val) => (
                                                        <label key={val} className="flex items-center gap-1.5 cursor-pointer">
                                                            <input
                                                                type="radio"
                                                                name="tl_volt_stage_1"
                                                                value={val}
                                                                checked={formValues.tl_volt_stage_1 === val}
                                                                onChange={() => handleInputChange("tl_volt_stage_1", val)}
                                                                className="w-4 h-4 accent-[#0052cc]"
                                                            />
                                                            <span className="text-sm text-slate-700 font-medium">{val}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                            <div className="w-px bg-slate-200 hidden md:block"></div>
                                            <div className="flex flex-col gap-2">
                                                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Voltage Stage 2 (kV)</label>
                                                <div className="flex gap-4 items-center">
                                                    {[220, 400].map((val) => (
                                                        <label key={val} className="flex items-center gap-1.5 cursor-pointer">
                                                            <input
                                                                type="radio"
                                                                name="tl_volt_stage_2"
                                                                value={val}
                                                                checked={formValues.tl_volt_stage_2 === val}
                                                                onChange={() => handleInputChange("tl_volt_stage_2", val)}
                                                                className="w-4 h-4 accent-[#0052cc]"
                                                            />
                                                            <span className="text-sm text-slate-700 font-medium">{val}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">
                                            {fieldConfig.losses.fields.map((field) => {
                                                const is220kv = field.name === "transmission_line_220kv_loss";
                                                const is400kv = field.name === "transmission_line_400kv_loss";
                                                const isTrans220 = field.name === "transformer_220kv_loss";
                                                const isTrans400 = field.name === "transformer_400kv_loss";

                                                let dynamicLabel = field.label;
                                                if (is220kv) dynamicLabel = `${formValues.tl_volt_stage_1}kV Transmission line loss (%)`;
                                                if (isTrans220) dynamicLabel = `${formValues.tl_volt_stage_1}/33 kV transformer loss (%)`;
                                                if (is400kv) dynamicLabel = `${formValues.tl_volt_stage_2}kV Transmission line loss (%)`;
                                                if (isTrans400) dynamicLabel = `${formValues.tl_volt_stage_2}/${formValues.tl_volt_stage_1}kV transformer loss (%)`;

                                                // Determine disabled state
                                                let isDisabled = false;
                                                if (is220kv) isDisabled = !formValues.use_220kv_loss;
                                                if (is400kv) isDisabled = !formValues.use_400kv_loss;
                                                if (isTrans220) isDisabled = !formValues.use_transformer_220_loss;
                                                if (isTrans400) isDisabled = !formValues.use_transformer_400_loss;

                                                return (
                                                    <div key={field.name} className="flex flex-col gap-1 relative">
                                                        <div className="flex items-center gap-2 h-4 mb-1">
                                                            {is220kv && (
                                                                <input
                                                                    type="checkbox"
                                                                    checked={formValues.use_220kv_loss}
                                                                    onChange={(e) => {
                                                                        const checked = e.target.checked;
                                                                        const nextValues = {
                                                                            ...formValues,
                                                                            use_220kv_loss: checked,
                                                                            transmission_line_220kv_loss: checked ? formValues.transmission_line_220kv_loss : ""
                                                                        };
                                                                        setFormValues(nextValues);
                                                                        if (touched.use_220kv_loss || touched.transmission_line_220kv_loss) {
                                                                            const validation = validateBessForm(nextValues);
                                                                            setErrors(validation.errors);
                                                                        }
                                                                    }}
                                                                    className="w-3.5 h-3.5 accent-[#0052cc] cursor-pointer"
                                                                />
                                                            )}
                                                            {is400kv && (
                                                                <input
                                                                    type="checkbox"
                                                                    checked={formValues.use_400kv_loss}
                                                                    onChange={(e) => {
                                                                        const checked = e.target.checked;
                                                                        const nextValues = {
                                                                            ...formValues,
                                                                            use_400kv_loss: checked,
                                                                            transmission_line_400kv_loss: checked ? formValues.transmission_line_400kv_loss : ""
                                                                        };
                                                                        setFormValues(nextValues);
                                                                        if (touched.use_400kv_loss || touched.transmission_line_400kv_loss) {
                                                                            const validation = validateBessForm(nextValues);
                                                                            setErrors(validation.errors);
                                                                        }
                                                                    }}
                                                                    className="w-3.5 h-3.5 accent-[#0052cc] cursor-pointer"
                                                                />
                                                            )}
                                                            {isTrans220 && (
                                                                <input
                                                                    type="checkbox"
                                                                    checked={formValues.use_transformer_220_loss !== false}
                                                                    onChange={(e) => {
                                                                        const checked = e.target.checked;
                                                                        const nextValues = {
                                                                            ...formValues,
                                                                            use_transformer_220_loss: checked,
                                                                            transformer_220kv_loss: checked ? formValues.transformer_220kv_loss : ""
                                                                        };
                                                                        setFormValues(nextValues);
                                                                        if (touched.use_transformer_220_loss || touched.transformer_220kv_loss) {
                                                                            const validation = validateBessForm(nextValues);
                                                                            setErrors(validation.errors);
                                                                        }
                                                                    }}
                                                                    className="w-3.5 h-3.5 accent-[#0052cc] cursor-pointer"
                                                                />
                                                            )}
                                                            {isTrans400 && (
                                                                <input
                                                                    type="checkbox"
                                                                    checked={Boolean(formValues.use_transformer_400_loss)}
                                                                    onChange={(e) => {
                                                                        const checked = e.target.checked;
                                                                        const nextValues = {
                                                                            ...formValues,
                                                                            use_transformer_400_loss: checked,
                                                                            transformer_400kv_loss: checked ? formValues.transformer_400kv_loss : ""
                                                                        };
                                                                        setFormValues(nextValues);
                                                                        if (touched.use_transformer_400_loss || touched.transformer_400kv_loss) {
                                                                            const validation = validateBessForm(nextValues);
                                                                            setErrors(validation.errors);
                                                                        }
                                                                    }}
                                                                    className="w-3.5 h-3.5 accent-[#0052cc] cursor-pointer"
                                                                />
                                                            )}
                                                            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider truncate select-none cursor-pointer" title={dynamicLabel}>
                                                                {dynamicLabel}
                                                            </span>
                                                        </div>
                                                        <div className="relative">
                                                            <input
                                                                type={field.type}
                                                                value={formValues[field.name]}
                                                                onChange={(e) => handleInputChange(field.name, field.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)}
                                                                onBlur={() => handleBlur(field.name)}
                                                                placeholder="0.00"
                                                                disabled={isDisabled}
                                                                style={{ paddingRight: field.units ? '28px !important' : '14px !important' }}
                                                                className={isDisabled ? "bg-slate-50 cursor-not-allowed text-slate-400 border-slate-200" : ""}
                                                            />
                                                            {field.units && (
                                                                <span className="absolute right-3.5 top-3 text-[10px] text-slate-400 font-bold">{field.units}</span>
                                                            )}
                                                        </div>
                                                        {touched[field.name] && errors[field.name] && (
                                                            <span className="text-[10px] text-red-500 font-semibold mt-1">
                                                                {errors[field.name]}
                                                            </span>
                                                        )}
                                                        {is220kv && touched.transmission_line_loss && errors.transmission_line_loss && !formValues.use_220kv_loss && (
                                                            <span className="text-[10px] text-red-500 font-semibold mt-1">
                                                                {errors.transmission_line_loss}
                                                            </span>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {currentStep === 2 && (
                                <div className="space-y-4 animate-fade-in">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch">
                                        {/* Core Parameters Card */}
                                        <div className="border border-slate-200 rounded-2xl p-4 bg-white shadow-sm flex flex-col justify-between">
                                            <div>
                                                <h4 className="font-bold text-[#0052CC] text-xs mb-2 uppercase tracking-wider">Core Parameters</h4>
                                                <div className="divide-y divide-slate-100 text-xs">
                                                    <div className="flex justify-between items-center py-1.5">
                                                        <span className="text-slate-500 font-medium">Simulation Name</span>
                                                        <span className="font-bold text-slate-800">{formValues.name || "—"}</span>
                                                    </div>
                                                    <div className="flex justify-between items-center py-1.5">
                                                        <span className="text-slate-500 font-medium">Battery Batch</span>
                                                        <span className="font-bold text-slate-800">
                                                            {containerOptions.find(c => c.id === formValues.container_batch)?.oem} ({parseFloat(containerOptions.find(c => c.id === formValues.container_batch)?.rated_capacity_mwh || 0).toFixed(2)} MWh)
                                                        </span>
                                                    </div>
                                                    <div className="flex justify-between items-center py-1.5">
                                                        <span className="text-slate-500 font-medium">PCS Model</span>
                                                        <span className="font-bold text-slate-800">
                                                            {pcsOptions.find(p => p.id === formValues.pcs)?.oem} - {pcsOptions.find(p => p.id === formValues.pcs)?.model}
                                                        </span>
                                                    </div>
                                                    <div className="flex justify-between items-center py-1.5">
                                                        <span className="text-slate-500 font-medium">Simulation Type</span>
                                                        <span className="font-bold text-slate-800">
                                                            {formValues.simulation_type === "closed_loop"
                                                                ? "Manual Battery Augmentation"
                                                                : formValues.simulation_type === "auto_closed_loop"
                                                                ? "Auto Battery Augmentation"
                                                                : "Without Battery Augmentation"}
                                                        </span>
                                                    </div>
                                                    <div className="flex justify-between items-center py-1.5">
                                                        <span className="text-slate-500 font-medium">Storage Duration</span>
                                                        <span className="font-bold text-slate-800">{formValues.operation_hour} Hours</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Simulation Settings Card */}
                                        <div className="border border-slate-200 rounded-2xl p-4 bg-white shadow-sm flex flex-col justify-between">
                                            <div>
                                                <h4 className="font-bold text-[#0052CC] text-xs mb-2 uppercase tracking-wider">Simulation Settings</h4>
                                                <div className="divide-y divide-slate-100 text-xs">
                                                    <div className="flex justify-between items-center py-1.5">
                                                        <span className="text-slate-500 font-medium">Commence Year</span>
                                                        <span className="font-bold text-slate-800">{formValues.start_year}</span>
                                                    </div>
                                                    <div className="flex justify-between items-center py-1.5">
                                                        <span className="text-slate-500 font-medium">Tenure</span>
                                                        <span className="font-bold text-slate-800">{formValues.simulation_tenure} Years</span>
                                                    </div>
                                                    <div className="flex justify-between items-center py-1.5">
                                                        <span className="text-slate-500 font-medium">Contract Target @ POI</span>
                                                        <span className="font-bold text-slate-800">{formValues.energy_poi_mwh} MWh</span>
                                                    </div>
                                                    <div className="flex justify-between items-center py-1.5">
                                                        <span className="text-slate-500 font-medium">Augmentation Purchase Years</span>
                                                        <span className="font-bold text-slate-800 text-right">
                                                            {formValues.simulation_type === "closed_loop"
                                                                ? (formValues.purchase_years.length > 0
                                                                    ? formValues.purchase_years.map(item => {
                                                                        if (typeof item === 'object' && item !== null) {
                                                                            return `Yr ${item.year}: ${item.containers} Ctr${item.containers > 1 ? 's' : ''}`;
                                                                        }
                                                                        return `Yr ${item}`;
                                                                    }).join(", ")
                                                                    : "None")
                                                                : "N/A"
                                                            }
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Quantities & Losses Card */}
                                    <div className="border border-slate-200 rounded-2xl p-4 bg-white shadow-sm">
                                        <h4 className="font-bold text-[#0052CC] text-xs mb-2 uppercase tracking-wider">Quantities & Losses</h4>
                                        <div className="grid grid-cols-3 gap-0 bg-[#f8fafc] border border-slate-100 rounded-xl p-3 text-xs">
                                            <div className="flex flex-col gap-1 px-4 border-r border-slate-200/60">
                                                <span className="text-slate-400 font-semibold text-[9px] uppercase tracking-wide">Initial Containers</span>
                                                <span className="text-base font-bold text-slate-800">{formValues.container_nos} Nos</span>
                                            </div>
                                            <div className="flex flex-col gap-1 px-6 border-r border-slate-200/60">
                                                <span className="text-slate-400 font-semibold text-[9px] uppercase tracking-wide">Initial PCS</span>
                                                <span className="text-base font-bold text-slate-800">{formValues.pcs_nos} Nos</span>
                                            </div>
                                            <div className="flex flex-col gap-1 px-6">
                                                <span className="text-slate-400 font-semibold text-[9px] uppercase tracking-wide">Plant Availability</span>
                                                <span className="text-base font-bold text-slate-800">{formValues.bess_plant_unavailability}%</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Ready to run block */}
                                    {/* <div className="bg-[#f8fafc] border border-slate-100 rounded-xl p-4 text-xs">
                                        <div className="font-bold text-[#0052cc] mb-0.5">Ready to run?</div>
                                        <div className="text-slate-500">You can go back to make changes or re-run the simulation with these settings.</div>
                                    </div> */}
                                </div>
                            )}

                            {/* Stepper Navigation Buttons */}
                            <div className="flex justify-between items-center pt-6 border-t border-slate-100 mt-8">
                                {currentStep === 1 ? (
                                    <>
                                        <div />
                                        <button
                                            type="button"
                                            onClick={handleContinue}
                                            className="bess-btn-primary"
                                        >
                                            Continue &rarr;
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <div className="flex items-center gap-3">
                                            <button
                                                type="button"
                                                onClick={handleBack}
                                                disabled={isRunning}
                                                className="h-10 px-5 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 transition-all cursor-pointer flex items-center gap-1.5 shadow-sm"
                                            >
                                                &larr; Back
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleReset}
                                                disabled={isRunning}
                                                className="h-10 px-5 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 transition-all cursor-pointer flex items-center shadow-sm"
                                            >
                                                Reset
                                            </button>
                                        </div>

                                        <button
                                            type="button"
                                            onClick={handleRunAnalysis}
                                            disabled={isRunning}
                                            className="h-10 px-6 bg-[#0052cc] hover:bg-[#0747a6] text-white rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 shadow-sm"
                                        >
                                            {isRunning ? <LoadingOutlined /> : null} {loadedFromHistory ? "Re-run Simulation" : "Run Simulation"} &rarr;
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                ) : (
                    /* Simulation History List Cards View */
                    <div className="space-y-6 animate-fade-in">
                        {/* Search bar row */}
                        <div className="w-full max-w-xs relative">
                            <input
                                type="text"
                                placeholder="Search Project"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="h-10 w-full pl-4 pr-10 border border-slate-200 rounded-lg text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 shadow-sm bg-white"
                            />
                            <SearchOutlined className="absolute right-3.5 top-3.5 text-slate-400 text-[13px]" />
                        </div>

                        {(() => {
                            const filteredSimulations = pastSimulations.filter((sim) => {
                                const query = searchQuery.trim().toLowerCase();
                                if (!query) return true;
                                const idStr = sim.id.toString();
                                const hashIdStr = `#${sim.id}`;
                                const nameStr = (sim.name || "").toLowerCase();
                                return nameStr.includes(query) || idStr.includes(query) || hashIdStr.includes(query);
                            });

                            if (filteredSimulations.length > 0) {
                                return (
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                        {filteredSimulations.map((sim) => {
                                            const dateObj = sim.ran_at ? new Date(sim.ran_at) : null;
                                            const formattedDate = dateObj
                                                ? `${dateObj.toLocaleDateString()} ${dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                                                : "Draft (Not Run)";
                                            const typeLabel = sim.simulation_type === "closed_loop"
                                                ? "Manual Battery Augmentation"
                                                : sim.simulation_type === "auto_closed_loop"
                                                ? "Auto Battery Augmentation"
                                                : "Without Battery Augmentation";
                                            const isMinMarginBreached = sim.ran_at && parseFloat(sim.min_poi_margin_mwh) < 0;

                                            const leftBorderColor = sim.ran_at
                                                ? (sim.simulation_type === "closed_loop"
                                                    ? "border-l-[#2563eb]"
                                                    : sim.simulation_type === "auto_closed_loop"
                                                    ? "border-l-[#059669]"
                                                    : "border-l-[#8b5cf6]")
                                                : "border-l-slate-300";

                                            const badgeClass = sim.simulation_type === "closed_loop"
                                                ? "bg-blue-50 text-blue-600 border border-blue-100"
                                                : sim.simulation_type === "auto_closed_loop"
                                                ? "bg-emerald-50 text-emerald-600 border border-emerald-100"
                                                : "bg-purple-50 text-purple-600 border border-purple-100";

                                            const containerObj = containerOptions.find(c => c.id === (typeof sim.container_batch === 'object' ? sim.container_batch?.id : sim.container_batch));
                                            const pcsObj = pcsOptions.find(p => p.id === (typeof sim.pcs === 'object' ? sim.pcs?.id : sim.pcs));

                                            const containerName = sim.container_batch_name || (containerObj
                                                ? `${containerObj.oem} (${parseFloat(containerObj.rated_capacity_mwh || 0).toFixed(2)} MWh)`
                                                : (sim.container_batch ? `Container #${sim.container_batch}` : "—"));

                                            const pcsName = sim.pcs_name || (pcsObj
                                                ? `${pcsObj.oem}${pcsObj.model ? ` - ${pcsObj.model}` : ''}`
                                                : (sim.pcs ? `PCS #${sim.pcs}` : "—"));

                                            return (
                                                <div
                                                    key={sim.id}
                                                    className={`p-6 border border-slate-200 border-l-4 ${leftBorderColor} rounded-2xl bg-white shadow-sm flex flex-col justify-between`}
                                                >
                                                    <div>
                                                        <div className="flex justify-between items-start gap-2">
                                                            <div className="flex-1 min-w-0">
                                                                <div className="font-bold text-sm text-slate-800 truncate" title={sim.name || `Simulation #${sim.id}`}>
                                                                    {sim.name || `Simulation #${sim.id}`}
                                                                </div>
                                                                <div className="text-[11px] text-slate-400 mt-1 font-medium">{formattedDate}</div>
                                                            </div>
                                                            <div className="flex items-center gap-1.5 shrink-0">
                                                                {sim.is_frozen ? (
                                                                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border bg-cyan-50 text-cyan-700 border-cyan-200 flex items-center gap-1" title="Simulation is frozen">
                                                                        <LockOutlined style={{ fontSize: '10px' }} /> Frozen
                                                                    </span>
                                                                ) : (
                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleFreezeSimulation(sim);
                                                                        }}
                                                                        className="text-slate-400 hover:text-blue-600 transition-colors p-1 rounded hover:bg-slate-50 cursor-pointer border-none bg-transparent flex items-center justify-center"
                                                                        title="Freeze Simulation"
                                                                    >
                                                                        <LockOutlined style={{ fontSize: '14px' }} />
                                                                    </button>
                                                                )}
                                                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${badgeClass}`}>
                                                                    {typeLabel}
                                                                </span>
                                                                <button
                                                                    type="button"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleDeleteSimulation(sim);
                                                                    }}
                                                                    className="text-slate-400 hover:text-red-500 transition-colors p-1 rounded hover:bg-slate-50 cursor-pointer border-none bg-transparent flex items-center justify-center"
                                                                    title="Delete Simulation"
                                                                >
                                                                    <DeleteOutlined style={{ fontSize: '14px' }} />
                                                                </button>
                                                            </div>
                                                        </div>

                                                        {/* Key Specs Grid */}
                                                        <div className="grid grid-cols-3 gap-2 mt-4 pt-3 border-t border-slate-100 text-xs">
                                                            <div className="flex flex-col">
                                                                <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">Tenure</span>
                                                                <span className="font-bold text-slate-800 text-[13px] mt-0.5">{sim.simulation_tenure} Yrs</span>
                                                            </div>
                                                            <div className="flex flex-col">
                                                                <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">POI Target</span>
                                                                <span className="font-bold text-slate-800 text-[13px] mt-0.5">{Number(sim.energy_poi_mwh).toFixed(1)} MWh</span>
                                                            </div>
                                                            <div className="flex flex-col">
                                                                <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">Augmentation</span>
                                                                <span className="font-bold text-slate-800 text-[13px] mt-0.5">
                                                                    {sim.ran_at ? `${sim.total_aug_ctrs} Ctrs` : "—"}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="grid grid-cols-2 gap-3 mt-5">
                                                        <button
                                                            type="button"
                                                            onClick={() => navigate(`/bess-sizing/${sim.id}`)}
                                                            disabled={!sim.ran_at}
                                                            className={`w-full py-2 border rounded-lg font-bold text-xs cursor-pointer transition-all shadow-sm flex items-center justify-center gap-1 ${sim.ran_at
                                                                ? "border-emerald-500 text-emerald-600 bg-white hover:bg-emerald-50/50"
                                                                : "border-slate-200 text-slate-300 bg-slate-50 cursor-not-allowed"
                                                                }`}
                                                        >
                                                            View Results
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleLoadSimulation(sim)}
                                                            className="w-full py-2 border border-blue-500 text-blue-600 bg-white hover:bg-blue-50/50 rounded-lg font-bold text-xs cursor-pointer transition-all shadow-sm flex items-center justify-center gap-1"
                                                        >
                                                            Load Config
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            } else {
                                return (
                                    <div className="text-center py-12 text-slate-400 text-sm italic bess-card">
                                        {searchQuery.trim() ? "No simulations match your search query." : "No past sizing simulations found."}
                                    </div>
                                );
                            }
                        })()}
                    </div>
                )}
            </div>
        </div>
    );
}

